import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
  warning: (msg: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * App-wide toast/snackbar. Replaces native window.alert() so success and
 * error feedback matches the design language instead of a browser dialog.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // No-op fallback so callers never crash if the provider is missing.
    const noop = () => {};
    return { success: noop, error: noop, info: noop, warning: noop };
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; msg: string; severity: Severity }>({
    open: false,
    msg: '',
    severity: 'info',
  });

  const show = useCallback((msg: string, severity: Severity) => {
    setState({ open: true, msg, severity });
  }, []);

  const api: ToastApi = {
    success: (m) => show(m, 'success'),
    error: (m) => show(m, 'error'),
    info: (m) => show(m, 'info'),
    warning: (m) => show(m, 'warning'),
  };

  const handleClose = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={4500}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={state.severity}
          variant="filled"
          onClose={handleClose}
          sx={{
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-e3)',
            fontWeight: 500,
            alignItems: 'center',
          }}
        >
          {state.msg}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}
